import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

interface StatCardProps {
  value: string;
  label: string;
  sub?: string;
  accent?: string;
}

export function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export const StatCard: React.FC<StatCardProps> = ({ value, label, sub, accent = 'var(--accent-green)' }) => (
  <motion.div
    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-bg-card p-6 transition duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-bg-elevated"
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.45 }}
  >
    <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
    <p className="font-heading text-[1.8rem] font-extrabold leading-none" style={{ color: accent }}>{value}</p>
    <p className="mt-2 text-sm font-medium text-text-primary">{label}</p>
    {sub && <p className="mt-1 text-xs text-text-muted">{sub}</p>}
  </motion.div>
);

interface SectionHeaderProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  center?: boolean;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({ eyebrow, title, subtitle, center }) => (
  <motion.div
    className={cx('mb-12', center ? 'text-center' : 'text-left')}
    initial={{ opacity: 0, y: 14 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.45 }}
  >
    {eyebrow && (
      <p className="mb-3 text-[0.72rem] uppercase tracking-[0.15em] text-accent-green">
        {eyebrow}
      </p>
    )}
    <h2 className={cx('text-[clamp(1.8rem,4vw,2.8rem)] font-extrabold leading-[1.1]', subtitle && 'mb-4')}>
      {title}
    </h2>
    {subtitle && <p className={cx('max-w-2xl text-[1.05rem] leading-7 text-text-secondary', center && 'mx-auto')}>{subtitle}</p>}
  </motion.div>
);

interface PageHeroProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  accent?: string;
  token?:Boolean;
}

export const PageHero: React.FC<PageHeroProps> = ({ eyebrow, title, subtitle, token,accent = 'var(--accent-green)' }) => (
  <section className="relative overflow-hidden border-b border-white/10 bg-[linear-gradient(180deg,rgba(0,232,123,0.04)_0%,transparent_100%)] px-8 pb-16 pt-[140px]">
    <div
      className="pointer-events-none absolute -right-[200px] -top-[200px] h-[600px] w-[600px] rounded-full animate-float"
      style={{ background: `radial-gradient(circle, ${accent}08 0%, transparent 70%)` }}
    />
    <div className="relative mx-auto max-w-7xl">
      <motion.p
        className="mb-4 text-[0.72rem] uppercase tracking-[0.15em]"
        style={{ color: accent }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {eyebrow}
      </motion.p>
      <motion.h1
        className="mb-6 max-w-3xl text-[clamp(2.5rem,6vw,4.5rem)] font-extrabold leading-[1.05]"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        {title}
      </motion.h1>
      <motion.p
        className="max-w-2xl text-[1.15rem] leading-8 text-text-secondary"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16 }}
      >
        {subtitle}
      </motion.p>

      {token &&(
             <Link to="/opportunity" className="inline-flex items-center gap-2 mt-4 rounded-xl bg-accent-green px-7 py-3.5 font-heading text-[0.95rem] font-bold text-bg-primary shadow-[0_0_30px_rgba(0,232,123,0.3)] transition hover:-translate-y-1">
                             Get AZT Token <ArrowRight size={18} />
              </Link>
      )}
    </div>
  </section>
);

interface DataTableProps {
  headers: string[];
  rows: (string | number)[][];
  accentCol?: number;
}

export const DataTable: React.FC<DataTableProps> = ({ headers, rows, accentCol = 0 }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-bg-elevated">
          {headers.map((h, i) => (
            <th key={i} className="border-b border-white/10 px-5 py-3.5 text-left font-heading text-[0.78rem] font-bold uppercase tracking-[0.05em] text-text-muted">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={cx(ri < rows.length - 1 && 'border-b border-white/10', ri % 2 === 0 ? 'bg-bg-card' : 'bg-bg-secondary')}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={cx(
                  'px-5 py-3.5 text-sm',
                  ci === 0 && 'font-semibold text-text-primary',
                  ci === accentCol && 'text-accent-green',
                  ci !== 0 && ci !== accentCol && 'text-text-secondary'
                )}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Card: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className, style }) => (
  <motion.div
    className={cx('rounded-[20px] border border-white/10 bg-bg-card p-8 transition duration-300 hover:-translate-y-1 hover:border-white/20', className)}
    style={style}
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.45 }}
  >
    {children}
  </motion.div>
);

export const GreenBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-block rounded-full border border-accent-green/25 bg-accent-green/10 px-2.5 py-1 text-xs font-semibold tracking-[0.05em] text-accent-green">
    {children}
  </span>
);

export const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mx-auto max-w-7xl px-8 py-16">
    {children}
  </div>
);
